<script setup lang="ts">
    import { computed, toRefs, ref, inject, watch  } from "vue";
    import { Link } from "@inertiajs/vue3";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import CloseIcon from "@icons/closeIcon.svg";
    let props = defineProps({
        userInfo: {
            type: Object,
            required: true
        }
    });

    let { canPlaceOrders, canHandleAdmin, canSeePricing } = storeToRefs(useSettingsStore());

    const openHeaderFlyout = defineModel("visible",{
        type: Boolean,
        default: false
    });

    let route = inject('route');

    let actions = computed(()=>[
        {
            label: "Dashboard",
            actionUrl: "dashboard",
            allowedUserTypes: ["dealer", "consumer"],
            requiredRole: "admin",
            alias: "account.show",
            visible: true,
        },
        {
            label: "Credit Cards",
            actionUrl: "cards",
            allowedUserTypes: ["dealer"],
            requiredRole: "admin",
            alias: "account.paymentMethods",
            visible: canPlaceOrders.value
        },
        {
            label: "Company Info",
            actionUrl: "company",
            allowedUserTypes: ["dealer"],
            requiredRole: "admin",
            alias: "account.companyInfo",
            visible: canHandleAdmin.value
        },
        {
            label: "Order History",
            actionUrl: "orders",
            allowedUserTypes: ["dealer", "consumer"],
            requiredRole: "admin",
            alias: "account.orders",
            visible: canPlaceOrders.value
        },
        {
            label: "Profile",
            actionUrl: "profile",
            allowedUserTypes: ["consumer"],
            requiredRole: "admin",
            alias: "account.profile",
        },  
        {
            label: "Password & Security",
            actionUrl: "password_security",
            allowedUserTypes: ["dealer","consumer"],
            requiredRole: "admin",
            alias: "account.passwordSecurity",
            visible: !isDealer.value
        },
        {
            label: "Address Book",
            actionUrl: "address_book",
            allowedUserTypes: ["consumer"],
            requiredRole: "admin",
            alias: "account.addresses",
        },
        {
            label: "Payment Methods",
            actionUrl: "payment_methods",
            allowedUserTypes: ["consumer"],
            requiredRole: "admin",
            alias: "account.paymentMethods",
            visible: canPlaceOrders.value
        },
        {
            label: "Payment History",
            actionUrl: "payments",
            allowedUserTypes: ["dealer"],
            requiredRole: "admin",
            alias: "account.paymentHistory",
            visible: canPlaceOrders.value
        },
        
        {
            label: "Favorites",
            actionUrl: "favorites",
            allowedUserTypes: ["dealer", "consumer"],
            requiredRole: "admin",
            alias: "account.favorites"
        },
        {
            label: "Saved Lists",
            actionUrl: "lists",
            allowedUserTypes: ["dealer"],
            requiredRole: "admin",
            alias: "account.lists"
        },
       
        {
            label: "User Accounts",
            actionUrl: "users",
            allowedUserTypes: ["dealer"],
            requiredRole: "admin",
            alias: "account.accounts",
            visible: canHandleAdmin.value
        },
        {
            label: "Password & Security",
            actionUrl: "password_security",
            allowedUserTypes: ["dealer","consumer"],
            requiredRole: "admin",
            alias: "account.passwordSecurity",
            visible: isDealer.value
        },
        {
            label: "API Portal Settings",
            actionUrl: "api_portal",
            allowedUserTypes: ["dealer"],
            requiredRole: "admin",
            alias: "account.apiPortal",
        },
        {
            label: "API Documentation",
            actionUrl: "api_documentation",
            allowedUserTypes: ["dealer"],
            requiredRole: "admin",
            alias: "api.docs",
            params: ["about-the-api"],
        }
       
    ].filter(link =>{
        if('visible' in link){
            return link.visible && (link.allowedUserTypes.includes(props.userInfo.isDealer ? "dealer" : "consumer"))
        }
        return link.allowedUserTypes.includes(props.userInfo.isDealer ? "dealer" : "consumer")
    }));

    // {
    //         label: "Logout",
    //         icon: SfIconLogout,
    //         actionUrl: "index/logout",
    //         allowedUserTypes: ["dealer", "consumer"],
    //         requiredRole: "all",
    //         alias: "web.logout",
    //         reload: ["user"]
    //     }
    const closeHeaderFlyout = () => {
        openHeaderFlyout.value = false;
    };

    let isDealer = computed(() => {
        return props.userInfo.isDealer;
    });

    let userType = computed(() => {
        return isDealer.value ? "dealer" : "consumer";
    });

    let allowedActions = computed(() => {
        return actions.value.filter((item) =>
            item.allowedUserTypes.includes(userType.value)
        );
    });
    
    const flyoutRef = ref(null);
    
    function getRoute(action) {
        if (action.params) {
            return route(action.alias, action.params);
        }
        return route(action.alias);
    }


    function setHeaderFlyoutVisible(value) {
        openHeaderFlyout.value = value;
    }
</script>

<template>

<PrimeDrawer
    :visible="openHeaderFlyout"
    @update:visible="setHeaderFlyoutVisible"
    position="right"
    :dismissable="true"
    class="user-menu"
    :pt="{
        transition: {
            name: 'slide'
        }
    }">
        <div class="user-menu-drawer" ref="flyoutRef">
            <div class="user-menu-header">
                <a @click.prevent="closeHeaderFlyout" href="#">
                    <CloseIcon alt="close" />
                </a>
            </div>
            <div class="user-menu-container">
                <div v-if="isDealer">
                    <span id="accountFlyoutGreeting" class="headline-s">My Account</span>
                    <div class="user-menu-details">
                        <span id="accountFlyoutEmail" class="paragraph-caption">{{ userInfo.email }}</span>
                        <span id="accountFlyoutDealerNo" class="paragraph-caption">Acct. # {{ userInfo.dealerNo }}</span>
                        <span id="accountFlyoutRole" class="paragraph-caption">{{ userInfo.role }}</span>
                    </div>
                </div>
                <div v-else>
                <span id="accountFlyoutGreeting" class="headline-s">Hi, {{userInfo.username}}!</span>
                    <!-- Loyalty points not yet implemented -->
                    <!-- <div class="user-menu-details">
                        <span class="paragraph-caption">Loyalty Status / Points</span>
                    </div> -->
                </div>

                <Link as="div" :onStart="closeHeaderFlyout" :href="getRoute(action)" :only="action.reload ? action.reload : []"
                    v-for="(action, index) of allowedActions" class="user-menu-container-item" :key="index">
                    <a>{{ action.label }}</a>
                </Link>

                <Link 
                    :href="route('web.logout')" 
                    class="user-menu-container-logout" :reload="['user']" :onStart="closeHeaderFlyout">Log Out</Link>
            </div>
        </div>
</PrimeDrawer>
    
</template>

<style scoped></style>
